<template>
  <q-dialog :value="value" @input="$emit('input', $event)" persistent @hide="resetForm">
    <q-card style="min-width: 520px; max-width: 90vw">
      <q-card-section>
        <div class="text-h6">{{ isEditing ? $t('edit_coupon') : $t('register_coupon') }}</div>
      </q-card-section>

      <q-card-section class="q-pt-none">
        <q-form @submit="onSubmit">
          <q-select
            v-model="form.type"
            :options="typeOptions"
            :label="$t('coupon_type') + '*'"
            outlined
            dense
            emit-value
            map-options
            class="q-mb-md"
            lazy-rules
            :rules="[isRequired]"
            @input="onTypeChange"
          />

          <q-input
            v-model="form.code"
            :label="$t('coupon_code') + '*'"
            outlined
            dense
            class="q-mb-md"
            lazy-rules
            :rules="[isRequired]"
            placeholder="JOAO10"
          />

          <q-select
            v-if="form.type === 'EXCLUSIVE'"
            v-model="form.site_id"
            :options="channelsFiltered"
            :label="$t('channel') + '*'"
            outlined
            dense
            use-input
            input-debounce="200"
            emit-value
            map-options
            clearable
            clear-icon="mdi-close"
            class="q-mb-md"
            option-value="site_id"
            option-label="site_name"
            lazy-rules
            :rules="[isRequired]"
            @filter="filterSites"
          />

          <div class="row q-col-gutter-md items-start q-mb-md">
            <div class="col-12 col-sm-6">
              <q-input
                v-model="form.valid_from"
                :label="$t('start_date')"
                outlined
                dense
                clearable
                mask="####-##-##"
                hide-bottom-space
                lazy-rules
                :rules="[isValidDate]"
              >
                <template v-slot:prepend>
                  <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                      <q-date v-model="form.valid_from" mask="YYYY-MM-DD">
                        <div class="row items-center justify-end">
                          <q-btn v-close-popup :label="$t('close')" color="primary" flat />
                        </div>
                      </q-date>
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>
            </div>
            <div class="col-12 col-sm-6">
              <q-input
                v-model="form.valid_until"
                :label="$t('end_date')"
                outlined
                dense
                clearable
                mask="####-##-##"
                hide-bottom-space
                lazy-rules
                :rules="[isValidDate, isEndDateValid]"
              >
                <template v-slot:prepend>
                  <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                      <q-date v-model="form.valid_until" mask="YYYY-MM-DD">
                        <div class="row items-center justify-end">
                          <q-btn v-close-popup :label="$t('close')" color="primary" flat />
                        </div>
                      </q-date>
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>
            </div>
          </div>

          <div class="row justify-end q-gutter-sm q-mt-md">
            <q-btn flat color="negative" :label="$t('cancel')" @click="close" />
            <q-btn flat color="primary" :label="$t('save')" type="submit" />
          </div>
        </q-form>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import AdvertiserService from '../../services/AdvertiserService';
import CampaignMixin from '../../mixins/CampaignMixin';

export default {
  name: 'CampaignCouponDialog',
  mixins: [AdvertiserService, CampaignMixin],
  props: {
    value: {
      type: Boolean,
      default: false
    },
    campaignId: {
      type: [String, Number],
      required: true
    },
    channelsList: {
      type: Array,
      default: () => []
    },
    coupon: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      form: this.getEmptyForm(),
      channelsFiltered: []
    };
  },
  computed: {
    isEditing() {
      return !!(this.coupon && this.coupon.id);
    },
    typeOptions() {
      return [
        { label: this.$t('exclusive'), value: 'EXCLUSIVE' },
        { label: this.$t('generic'), value: 'GENERIC' }
      ];
    }
  },
  watch: {
    coupon: {
      immediate: true,
      handler(coupon) {
        if (coupon && coupon.id) {
          this.form = {
            code: coupon.code,
            type: coupon.type,
            site_id: coupon.site_id,
            valid_from: coupon.valid_from,
            valid_until: coupon.valid_until
          };
        } else {
          this.form = this.getEmptyForm();
        }
      }
    },
    channelsList: {
      immediate: true,
      handler(val) {
        this.channelsFiltered = val;
      }
    }
  },
  methods: {
    getEmptyForm() {
      return {
        code: '',
        type: 'GENERIC',
        site_id: null,
        valid_from: '',
        valid_until: ''
      };
    },

    isRequired(val) {
      return (val !== null && val !== undefined && val !== '') || this.$t('required_field');
    },

    isValidDate(val) {
      if (!val) return true;
      return /^\d{4}-\d{2}-\d{2}$/.test(val) || this.$t('invalid_date');
    },

    isEndDateValid(val) {
      if (!this.form.valid_from || !val) return true;
      return val >= this.form.valid_from || this.$t('end_date_must_be_after_start_date');
    },

    onTypeChange(type) {
      if (type !== 'EXCLUSIVE') {
        this.form.site_id = null;
      }
    },

    filterSites(val, update) {
      update(() => {
        const needle = val.toLowerCase();
        if (!needle) {
          this.channelsFiltered = this.channelsList;
        } else {
          this.channelsFiltered = this.channelsList.filter((v) => v.site_name.toLowerCase().includes(needle));
        }
      });
    },

    resetForm() {
      this.form = this.getEmptyForm();
    },

    close() {
      this.$emit('input', false);
    },

    async onSubmit() {
      this.onLoading(true);
      try {
        const payload = {
          campaign_id: this.campaignId,
          code: this.form.code.trim(),
          type: this.form.type,
          site_id: this.form.type === 'EXCLUSIVE' ? this.form.site_id : null,
          valid_from: this.form.valid_from || null,
          valid_until: this.form.valid_until || null
        };

        let response;
        if (this.isEditing) {
          response = await this.updateCampaignCoupon(this.coupon.id, payload);
        } else {
          response = await this.createCampaignCoupon(payload);
        }

        if ([200, 201].includes(response.status)) {
          this.successNotify(
            this.isEditing ? this.$t('coupon_updated_successfully') : this.$t('coupon_created_successfully')
          );
          this.close();
          this.$emit('saved');
        }
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : this.$t('problem_try_again_later');
        this.errorNotify(message);
      } finally {
        this.onLoading(false);
      }
    }
  }
};
</script>
